import NewLanguageHome from "./pages/NewLanguageHome";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import DeferredPageBoundary from "./components/DeferredPageBoundary";
import { getPrivatePageSeo } from "./seo/privatePageSeo";
import { fetchWorldcupRelatedCards } from "./utils/worldcupHomeApi";
import WorldcupRouteDataLoader from "./components/WorldcupRouteDataLoader";
import { subscribeToAuthProfile } from "./utils/authSession";
import categoryLabels from "./seo/categoryLabels.json";
import NotFoundPage from "./components/NotFoundPage";
import WorldcupDetailSeo from "./seo/WorldcupDetailSeo";
import worldcupMetadata from "./seo/worldcupMetadata.js";
import "./i18n";
import "./App.css";
import React, { useState, useEffect, useCallback, useRef, createContext, useContext, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "./components/Header";
import ContentNav from "./components/ContentNav";
import Home from "./components/Home";
import SignupBox from "./components/SignupBox";
import LoginBox from "./components/LoginBox";
import FindIdBox from "./components/FindIdBox";
import FindPwBox from "./components/FindPwBox";
import Footer from "./components/Footer";
import SEOManager from "./seo/SEOManager";
import AdGuard from "./ads/AdGuard";
import { AdExclusionProvider } from "./ads/AdExclusionProvider";
import {
  getWorldcupTitle,
} from "./utils/localization";

import DePage from "./pages/de";
import EnPage from "./pages/en";
import EsPage from "./pages/es";
import FrPage from "./pages/fr";
import HiPage from "./pages/hi";
import IdPage from "./pages/id";
import JaPage from "./pages/ja";
import KoPage from "./pages/ko";
import PtPage from "./pages/pt";
import RuPage from "./pages/ru";
import ViPage from "./pages/vi";
import ZhPage from "./pages/zh";
import ArPage from "./pages/ar";
import BnPage from "./pages/bn";
import ThPage from "./pages/th";
import TrPage from "./pages/tr";

import {
  getWorldcupGames,
  deleteWorldcupGame,
  softDeleteWorldcupGame,
  getWorldcupGame,
} from "./utils/supabaseWorldcupApi";

import { supabase } from "./utils/supabaseClient";
import Seo from "./seo/Seo";
import { DEFAULT_QUIZ_LANGUAGE } from "./seo/quizSeo";

function LegacyTopNineRedirect() {
  const location = useLocation();
  return <Navigate to={`${location.pathname.replace(/\/top-nine\/?$/, '/mytop9')}${location.search}${location.hash}`} replace />;
}

function LegacyQuizRedirect() {
  const location = useLocation();
  return <Navigate to={`/${DEFAULT_QUIZ_LANGUAGE}${location.pathname.replace(/\/+$/, "")}${location.search}${location.hash}`} replace />;
}

/* =====================================================
   모바일 여부
===================================================== */

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= 700
      : false
  );

  useEffect(() => {
    function onResize() {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth <= 700);
      }
    }

    window.addEventListener("resize", onResize);

    return () =>
      window.removeEventListener("resize", onResize);
  }, []);

  return isMobile;
}

/* =====================================================
   현재 언어를 포함한 URL 생성
===================================================== */

function getLangPath(i18n, path = "") {
  const lang = (i18n.language || "en").split("-")[0];

  if (path.startsWith("/")) {
    path = path.slice(1);
  }

  return `/${lang}${path ? "/" + path : ""}`;
}

/* =====================================================
   언어별 홈 페이지
===================================================== */

function LanguageWrapper(props) {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const supportedLangs = [
      "ko",
      "en",
      "ru",
      "ja",
      "zh",
      "pt",
      "es",
      "fr",
      "id",
      "hi",
      "de",
      "vi",
      "ar",
      "bn",
      "th",
      "tr",
     "it", "pl", "nl", "cs"];

    if (!supportedLangs.includes(lang)) {
      navigate("/en", {
        replace: true,
      });

      return;
    }

    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, navigate]);

  const homeProps = {
    ...props,
  };

  switch (lang) {
    case "it": case "pl": case "nl": case "cs":
      return <NewLanguageHome {...homeProps} language={lang} />;
    case "ko":
      return <KoPage {...homeProps} />;

    case "en":
      return <EnPage {...homeProps} />;

    case "ru":
      return <RuPage {...homeProps} />;

    case "ja":
      return <JaPage {...homeProps} />;

    case "zh":
      return <ZhPage {...homeProps} />;

    case "pt":
      return <PtPage {...homeProps} />;

    case "es":
      return <EsPage {...homeProps} />;

    case "fr":
      return <FrPage {...homeProps} />;

    case "id":
      return <IdPage {...homeProps} />;

    case "hi":
      return <HiPage {...homeProps} />;

    case "de":
      return <DePage {...homeProps} />;

    case "vi":
      return <ViPage {...homeProps} />;

    case "ar":
      return <ArPage {...homeProps} />;

    case "bn":
      return <BnPage {...homeProps} />;

    case "th":
      return <ThPage {...homeProps} />;

    case "tr":
      return <TrPage {...homeProps} />;

    default:
      return <EnPage {...homeProps} />;
  }
}

/* =====================================================
   페이지 이동 시 스크롤 최상단
===================================================== */

// Home, header and login remain eager; route-only screens load on entry.
const PrivateContentCards = lazy(() => import("./components/PrivateContentCards"));
const MyWorldcupCards = lazy(() => import("./components/MyWorldcupCards"));
const BlogEditorPage = lazy(() => import("./components/BlogEditorPage"));
const CommunityBoardPage = lazy(() => import("./components/CommunityBoardPage"));
const SelectRoundPage = lazy(() => import("./components/SelectRoundPage"));
const MatchPage = lazy(() => import("./components/MatchPage"));
const ResultPage = lazy(() => import("./components/ResultPage"));
const StatsPage = lazy(() => import("./components/StatsPage"));
const WorldcupMaker = lazy(() => import("./components/WorldcupMaker"));
const BackupPage = lazy(() => import("./components/BackupPage"));
const ManageWorldcup = lazy(() => import("./components/ManageWorldcup"));
const EditWorldcupPage = lazy(() => import("./components/EditWorldcupPage"));
const AdminBar = lazy(() => import("./components/AdminBar"));
const TopNinePage = lazy(() => import("./components/TopNinePage"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const AdminStatsPage = lazy(() => import("./components/AdminStatsPage"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const SuggestionsBoard = lazy(() => import("./components/SuggestionsBoard"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const BlogPostPage = lazy(() => import("./components/BlogPostPage"));
const CategoryPage = lazy(() => import("./components/CategoryPage"));
const TierListPage = lazy(() => import("./components/TierListPage"));
const TierListMaker = lazy(() => import("./components/TierListMaker"));
const TierListResultPage = lazy(() => import("./components/TierListResultPage"));
const QuizPage = lazy(() => import("./components/QuizPage"));
const QuizMaker = lazy(() => import("./components/QuizMaker"));
const QuizDetailPage = lazy(() => import("./components/QuizDetailPage"));

const RandomPickPage = lazy(() => import('./components/randomPick/RandomPickPage'));

function ScrollToTopOnRouteChange() {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    const samePage = previousPath.current === location.pathname;
    previousPath.current = location.pathname;
    if (samePage && location.state?.preserveScroll) return;
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch (e) {
      // 무시
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [
    location.pathname,
    location.search,
    location.hash,
  ]);

  return null;
}
function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      aria-label="Back to top"
      onClick={() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }}
className="scroll-top-button"
style={{
  position: "fixed",
  right: 24,
  bottom: 130,

  width: 72,
  height: 72,

  borderRadius: "50%",
  border: "3px solid #ffd400",

  background:
    "#6650d8",

  color: "#ffffff",
  fontSize: 42,
  fontWeight: 900,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  cursor: "pointer",
  zIndex: 9999,

  boxShadow:
    "0 4px 16px rgba(25,32,52,0.07)",

  transition: "transform 0.2s ease",
}}
    >
      ↑
    </button>
  );
}
/* =====================================================
   APP
===================================================== */

function RouteBackground() { return null; }

const AppShellContext = createContext(null);
const AppRoutesContext = createContext(null);

function MyWorldcupsWrapper() {
  const { user, handleWorldcupDelete } = useContext(AppShellContext);
  return <MyWorldcupCards user={user} onDelete={id=>handleWorldcupDelete(id,{refresh:false})} />;
}

function RecentWorldcupsWrapper() {
  const { fetchWorldcups, handleWorldcupDelete, i18n, isAdmin, nickname, t, user, worldcupList } = useContext(AppShellContext);

    const navigate =
      useNavigate();

    let recents = [];

    try {
      recents =
        JSON.parse(
          localStorage.getItem(
            "onepickgame_recentWorldcups"
          ) || "[]"
        );
    } catch (error) {
      console.error(
        "최근 월드컵 목록 파싱 실패:",
        error
      );
    }

    recents = recents
      .reverse()
      .filter(
        (id, i, arr) =>
          arr.indexOf(id) === i
      );

    const recentCups =
      recents
        .map((id) =>
          worldcupList.find(
            (w) =>
              String(w.id) ===
              String(id)
          )
        )
        .filter(Boolean);

    return (
      <Home
        worldcupList={
          recentCups
        }
        fetchWorldcups={
          fetchWorldcups
        }
        onSelect={(cup) => {
          navigate(
            getLangPath(
              i18n,
              `select-round/${cup.id}`
            )
          );
        }}
        user={user}
        nickname={nickname}
        isAdmin={isAdmin}
        showFixedWorldcups={
          false
        }
        onDelete={async (id) => {
          try {
            await handleWorldcupDelete(
              id
            );
          } catch (e) {
            console.error(
              "삭제 실패:",
              e
            );

            alert(
              (t(
                "delete_failed"
              ) ||
                "Delete failed!") +
                " " +
                (e.message || e)
            );
          }
        }}
      />
    );
  }

function StatsPageWrapper() {
  const { worldcupList } = useContext(AppShellContext);

    const { id } =
      useParams();

    const { t } =
      useTranslation();

    const [
      cup,
      setCup,
    ] = useState(null);

    const [
      loading,
      setLoading,
    ] = useState(true);

    useEffect(() => {
      let mounted = true;

      async function fetchCup() {
        setLoading(true);

        let found =
          worldcupList.find(
            (c) =>
              String(c.id) ===
              String(id)
          );

        if (found) {
          if (mounted) {
            setCup(found);
            setLoading(false);
          }

          return;
        }

        try {
          const data =
            await getWorldcupGame(
              id
            );

          if (mounted) {
            setCup(data);
            setLoading(false);
          }
        } catch (error) {
          console.error(
            "통계용 월드컵 조회 실패:",
            error
          );

          if (mounted) {
            setCup(null);
            setLoading(false);
          }
        }
      }

      fetchCup();

      return () => {
        mounted = false;
      };
    }, [
      id,
      worldcupList,
    ]);

    if (loading) {
      return (
        <div
          style={{
            padding: 60,
            textAlign: "center",
          }}
        >
          {t("loading")}
        </div>
      );
    }

    if (!cup) {
      return (
        <div
          style={{
            padding: 60,
            textAlign: "center",
            color: "#b42346",
          }}
        >
          {t(
            "error_no_data"
          )}
        </div>
      );
    }

    return (
      <StatsPage
        selectedCup={cup}
        showCommentBox={
          true
        }
      />
    );
  }

function AppRoutes() {
  const { fetchWorldcups, fixedWorldcups, handleBackup, handleLangChange, handleRestore, handleWorldcupDelete, isAdmin, nickname, nicknameLoading, relatedWorldcupsById, setUser, setWorldcupAllLoaded, setWorldcupList, t, updateNickname, user, worldcupList } = useContext(AppShellContext);

    const navigate =
      useNavigate();

    const location =
      useLocation();

    const { i18n } =
      useTranslation();

    const langMatch =
      location.pathname.match(
        /^\/([a-z]{2})(\/|$)/
      );

    const currentLang =
      (
        langMatch
          ? langMatch[1]
          : i18n.language || "en"
      ).split("-")[0];

    const titleMap = {
      en: "Bracket Game | Ideal Type World Cup | One Pick Game",
      ko: "이상형 월드컵 | One Pick Game",
      ja: "トーナメントゲーム | One Pick Game",
      fr: "Tournoi de Vote | One Pick Game",
      es: "Torneo de Votación | One Pick Game",
      de: "Abstimmungsturnier | One Pick Game",
      pt: "Torneio de Votação | One Pick Game",
      ru: "Турнир голосований | One Pick Game",
      id: "Turnamen Voting | One Pick Game",
      hi: "वोटिंग टूर्नामेंट | One Pick Game",
      vi: "Giải đấu bình chọn | One Pick Game",
      zh: "淘汰赛游戏 | One Pick Game",
      ar: "بطولة التصويت | One Pick Game",
      bn: "ভোটিং টুর্নামেন্ট | One Pick Game",
      th: "เกมโหวตแบบทัวร์นาเมนต์ | One Pick Game",
      tr: "Turnuva Oyunu | One Pick Game",
    
"it":"Gioco di tornei | torneo preferito a scelta | One Pick Game",
"pl":"turniej gra ulubiony-wybór turniej One Pick Game",
"nl":"toernooi spel . . favoriete-keuze toernooi . . One Pick Game",
"cs":"turnaj Game Oblíbený výběr turnaj ? One Pick Game"
};

const descMap = {
  en: "Create and play tournament bracket games on One Pick Game. Vote for your favorites, build your own brackets, and discover community rankings.",

  ko: "이상형 월드컵을 만들고 다양한 토너먼트 게임을 플레이해보세요. 좋아하는 후보를 선택하고 직접 월드컵을 만들며 인기 순위도 확인할 수 있습니다.",

  ja: "投票トーナメントを作成してプレイしよう。お気に入りを選んで対戦を楽しみ、自分だけのトーナメントを作成したり、人気ランキングをチェックできます。",

  zh: "创建并游玩投票淘汰赛，选择你最喜欢的候选项进行对决，还可以创建自己的比赛并查看社区人气排名。",

  es: "Crea y juega torneos de votación en One Pick Game. Elige tus favoritos, crea tus propios torneos y descubre los rankings de la comunidad.",

  fr: "Créez et jouez à des tournois de vote sur One Pick Game. Choisissez vos favoris, créez vos propres tournois et découvrez les classements de la communauté.",

  de: "Erstelle und spiele Abstimmungsturniere auf One Pick Game. Wähle deine Favoriten, erstelle eigene Turniere und entdecke die Ranglisten der Community.",

  pt: "Crie e jogue torneios de votação no One Pick Game. Escolha seus favoritos, monte seus próprios torneios e descubra os rankings da comunidade.",

  ru: "Создавайте и играйте в турниры с голосованием на One Pick Game. Выбирайте фаворитов, создавайте собственные турниры и смотрите рейтинги сообщества.",

  id: "Buat dan mainkan turnamen voting di One Pick Game. Pilih favoritmu, buat turnamen sendiri, dan lihat peringkat komunitas.",

  hi: "One Pick Game पर वोटिंग टूर्नामेंट बनाएं और खेलें। अपने पसंदीदा विकल्प चुनें, अपना टूर्नामेंट बनाएं और कम्युनिटी रैंकिंग देखें।",

  vi: "Tạo và chơi các giải đấu bình chọn trên One Pick Game. Chọn mục yêu thích, tạo giải đấu của riêng bạn và khám phá bảng xếp hạng cộng đồng.",

  ar: "أنشئ والعب بطولات التصويت على One Pick Game. اختر مفضلاتك، وأنشئ بطولاتك الخاصة، واكتشف تصنيفات المجتمع.",

  bn: "One Pick Game-এ ভোটিং টুর্নামেন্ট তৈরি করুন ও খেলুন। আপনার পছন্দের প্রতিযোগী বেছে নিন, নিজের টুর্নামেন্ট তৈরি করুন এবং কমিউনিটি র‍্যাঙ্কিং দেখুন।",

  th: "สร้างและเล่นเกมโหวตแบบทัวร์นาเมนต์บน One Pick Game เลือกรายการโปรด สร้างทัวร์นาเมนต์ของคุณเอง และดูอันดับยอดนิยมจากชุมชน",

  tr: "One Pick Game'de oylama turnuvaları oluştur ve oyna. Favorilerini seç, kendi turnuvanı oluştur ve topluluk sıralamalarını keşfet.",

"it":"Crea e gioca ai tornei su One Pick Game. Vota i tuoi preferiti, costruisci i tuoi tornei e scopri la classifica della comunità.",
"pl":"Tworzenie i grać w turniejach turniejowych na One Pick Game. Głosuj na swoich ulubionych, budować własne turnieje i odkryć rankingi społeczności.",
"nl":"Maak en speel toernooien toernooien op One Pick Game. Stem op je favorieten, bouw je eigen toernooien, en ontdek community rankings.",
"cs":"Vytvořit a hrát turnajové hry na One Pick Game. Hlasovat pro své oblíbené, vybudovat své vlastní turnaje, a objevte komunitní žebříčky."
};

    /* ===============================================
       월드컵 만들기
    =============================================== */

function handleMakeWorldcup() {
  navigate(
    `/${currentLang}/worldcup-maker`
  );
}

    /* ===============================================
       메인 홈
    =============================================== */

    

    /* ===============================================
       라운드 선택
    =============================================== */

    
        /* ===============================================
       월드컵 만들기 래퍼
    =============================================== */

    

    /* ===============================================
       월드컵 관리 래퍼
    =============================================== */

    

    /* ===============================================
       월드컵 수정 래퍼
    =============================================== */

    

    /* ===============================================
       관리자 대시보드
    =============================================== */

    

    /* ===============================================
       관리자 통계
    =============================================== */

    

    /* ===============================================
       구 stats 주소 → result로 이동
    =============================================== */

    

    /* ===============================================
       ROUTES
    =============================================== */

    return (
      <AppRoutesContext.Provider value={{ currentLang, handleMakeWorldcup, i18n, navigate }}><>
        <SEOManager />

<div
  className="header-wrapper"
  style={{
    margin: 0,
    padding: 0,
    width: "100%",
    maxWidth: "none",
  }}
>
          <Header
            onLangChange={
              handleLangChange
            }
            onBackup={
              handleBackup
            }
            onRestore={
              handleRestore
            }
            onMakeWorldcup={
              handleMakeWorldcup
            }
            isAdmin={
              isAdmin
            }
            user={user}
            nickname={
              nickname
            }
            nicknameLoading={
              nicknameLoading
            }
            setUser={
              setUser
            }
            setNickname={
              updateNickname
            }
          />
        </div>

<AdGuard
  isAdmin={
    isAdmin
  }
/>

<div className="main-content-box">
  <DeferredPageBoundary>
  <Routes>
    <Route path="/auth/callback" element={<GoogleAuthCallback setNickname={updateNickname} />} />
    <Route path="/:lang/suggestions-board" element={<SuggestionsBoard user={user} />} />
    <Route path="/:lang/admin-suggestions" element={<SuggestionsBoard user={user} adminView />} />
    <Route path="/:lang/notices" element={<CommunityBoardPage board="notices" user={user} />} />
    <Route path="/:lang/community" element={<CommunityBoardPage board="community" user={user} />} />
    <Route path="/:lang/blog/write" element={<BlogEditorPage />} />
    <Route path="/:lang/blog/:slug/edit" element={<BlogEditorPage />} />
            <Route path="/quiz/*" element={<LegacyQuizRedirect />} />
            <Route
              path="/privacy-policy"
              element={
                <PrivacyPolicy />
              }
            />

            <Route
              path="/terms-of-service"
              element={
                <TermsOfService />
              }
            />

            <Route
              path="/suggestions-board"
              element={
                <SuggestionsBoard
                  user={
                    user
                  }
                  isAdmin={
                    isAdmin
                  }
                />
              }
            />

            <Route
              path="/signup"
              element={
                <Navigate
                  to="/en/signup"
                  replace
                />
              }
            />

            <Route
              path="/find-id"
              element={
                <Navigate
                  to="/en/find-id"
                  replace
                />
              }
            />

            <Route
              path="/find-pw"
              element={
                <Navigate
                  to="/en/find-pw"
                  replace
                />
              }
            />

            <Route
              path="/:lang/blog"
              element={
                <BlogPage />
              }
            />

<Route
  path="/:lang/blog/:slug"
  element={
    <BlogPostPage />
  }
/>

<Route
  path="/:lang/category/:categorySlug"
  element={
<CategoryPage
  worldcupList={worldcupList}
  onDelete={handleWorldcupDelete}
  user={user}
  isAdmin={isAdmin}
/>
  }
/>
<Route path="/:lang/mytop9" element={<TopNinePage />} />
<Route path="/:lang/top-nine" element={<LegacyTopNineRedirect />} />
<Route path="/:lang/random-pick" element={<RandomPickPage user={user} />} />
<Route path="/:lang/random-pick/:mode" element={<RandomPickPage user={user} />} />
<Route
  path="/:lang/tier-list"
  element={
    new URLSearchParams(location.search).get('mine') === '1'
      ? <PrivateContentCards kind="tier-list" user={user} />
      : <TierListPage worldcupList={worldcupList} />
  }
/>
<Route
  path="/:lang/tier-list/create"
  element={
    <TierListMaker
      worldcupList={worldcupList}
    />
  }
/>

<Route
  path="/:lang/tier-list/create/:id"
  element={
    <TierListMaker
      worldcupList={worldcupList}
    />
  }
/>
<Route
  path="/:lang/tier-list/:id"
  element={<TierListResultPage />}
/>
<Route
  path="/:lang/quiz"
  element={new URLSearchParams(location.search).get('mine')==='1'?<PrivateContentCards kind="quiz" user={user}/>:<QuizPage user={user} />}
/>
<Route
  path="/:lang/quiz/create"
  element={<QuizMaker />}
/>
<Route
  path="/:lang/quiz/:id"
  element={<QuizDetailPage />}
/>
<Route
  path="/:lang"
  element={Object.prototype.hasOwnProperty.call(categoryLabels, location.pathname.split("/")[1]) ? (
                <>
                                    <LanguageWrapper
                    worldcupList={
                      worldcupList
                    }
                    fetchWorldcups={
                      fetchWorldcups
                    }
                    onMakeWorldcup={
                      handleMakeWorldcup
                    }
                    onDelete={async (
                      id
                    ) => {
                      try {
                        await handleWorldcupDelete(
                          id
                        );
                      } catch (e) {
                        console.error(
                          "언어 홈 삭제 실패:",
                          e
                        );

                        alert(
                          (t(
                            "delete_failed"
                          ) ||
                            "Delete failed!") +
                            " " +
                            (e.message ||
                              e)
                        );
                      }
                    }}
                    user={
                      user
                    }
                    nickname={
                      nickname
                    }
                    isAdmin={
                      isAdmin
                    }
                    fixedWorldcups={
                      fixedWorldcups
                    }
                  />
                </>
              ) : <NotFoundPage />}
            />

{/* A game ID is required for these pages. */}
<Route
  path="/:lang/select-round"
  element={<NotFoundPage />}
/>

<Route
  path="/:lang/result"
  element={<NotFoundPage />}
/>

            <Route
              path="/:lang/select-round/:id"
              element={
                <SelectRoundPageWrapper />
              }
            />

            <Route
              path="/:lang/match/:id/:round"
              element={
                <MatchPage
                  worldcupList={
                    worldcupList
                  }
                />
              }
            />

            <Route
              path="/:lang/result/:id"
              element={
                <ResultPage
                  worldcupList={
                    worldcupList
                  }
                />
              }
            />

            <Route
              path="/:lang/result/:id/:round"
              element={
                <ResultPage
                  worldcupList={
                    worldcupList
                  }
                />
              }
            />

            <Route
              path="/:lang/stats/:id"
              element={
                <RedirectStatsToResult />
              }
            />

            <Route
              path="/:lang/worldcup-maker"
              element={
                <WorldcupMakerWrapper />
              }
            />

            <Route
              path="/:lang/manage"
              element={
                <ManageWorldcupWrapper />
              }
            />

            <Route
              path="/:lang/backup"
              element={
                <BackupPage
                  worldcupList={
                    worldcupList
                  }
                  setWorldcupList={
                    setWorldcupList
                  }
                />
              }
            />

            <Route
              path="/:lang/edit-worldcup/:id"
              element={
                <EditWorldcupPageWrapper />
              }
            />
                        <Route
              path="/:lang/admin"
              element={
                <AdminRoute />
              }
            />

            <Route
              path="/:lang/admin-stats"
              element={
                <AdminStatsRoute />
              }
            />

            <Route
              path="/:lang/signup"
              element={
                <SignupBox />
              }
            />

            <Route
              path="/:lang/login"
              element={
                <LoginBox
                  setUser={
                    setUser
                  }
                  setNickname={
                    updateNickname
                  }
                />
              }
            />

            <Route
              path="/:lang/find-id"
              element={
                <FindIdBox />
              }
            />

            <Route
              path="/:lang/find-pw"
              element={
                <FindPwBox />
              }
            />

            <Route
              path="/:lang/reset-password"
              element={
                <Navigate
                  to="/en"
                />
              }
            />

            <Route
              path="/:lang/privacy-policy"
              element={
                <PrivacyPolicy />
              }
            />

            <Route
              path="/:lang/terms-of-service"
              element={
                <TermsOfService />
              }
            />

            <Route
              path="/:lang/suggestions"
              element={
                <SuggestionsBoard
                  user={
                    user
                  }
                  isAdmin={
                    isAdmin
                  }
                />
              }
            />

            <Route
              path="/:lang/my-worldcups"
              element={
                <MyWorldcupsWrapper />
              }
            />

            <Route
              path="/:lang/recent-worldcups"
              element={
                <RecentWorldcupsWrapper />
              }
            />

            <Route
              path="/"
              element={<HomeWrapper />}
            />

            <Route
              path="*"
              element={
                <NotFoundPage />
              }
            />
          </Routes>
          </DeferredPageBoundary>
        </div>
      </></AppRoutesContext.Provider>
    );
  }

function HomeWrapper() {
  const { fetchWorldcups, fixedWorldcups, handleWorldcupDelete, isAdmin, nickname, t, user, worldcupList } = useContext(AppShellContext);

  const { handleMakeWorldcup, i18n, navigate } = useContext(AppRoutesContext);

      return (
        <EnPage
          worldcupList={
            worldcupList
          }
          fetchWorldcups={
            fetchWorldcups
          }
          onSelect={(cup) => {
            let recent = [];

            try {
              recent =
                JSON.parse(
                  localStorage.getItem(
                    "onepickgame_recentWorldcups"
                  ) || "[]"
                );
            } catch (error) {
              console.error(
                "최근 목록 파싱 실패:",
                error
              );
            }

            localStorage.setItem(
              "onepickgame_recentWorldcups",
              JSON.stringify(
                [
                  cup.id,
                  ...recent.filter(
                    (id) =>
                      id !== cup.id
                  ),
                ].slice(
                  0,
                  30
                )
              )
            );

            navigate(
              getLangPath(
                i18n,
                `select-round/${cup.id}`
              )
            );
          }}
          onMakeWorldcup={
            handleMakeWorldcup
          }
          onDelete={async (id) => {
            try {
              await handleWorldcupDelete(
                id
              );
            } catch (e) {
              console.error(
                "홈 삭제 실패:",
                e
              );

              alert(
                (t(
                  "delete_failed"
                ) ||
                  "Delete failed!") +
                  " " +
                  (e.message || e)
              );
            }
          }}
          user={user}
          nickname={nickname}
          isAdmin={isAdmin}
          fixedWorldcups={
            fixedWorldcups
          }
        />
      );
    }

function SelectRoundPageWrapper() {
  const { handleWorldcupDelete, isAdmin, relatedWorldcupsById, t, user, worldcupList } = useContext(AppShellContext);

  const { i18n, navigate } = useContext(AppRoutesContext);

      const {
        id,
        lang = "en",
      } = useParams();

      const cup =
        worldcupList.find(
          (c) =>
            String(c.id) ===
            String(id)
        );

      if (!cup) {
        return (
          <div
            style={{
              minHeight: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "center",
              color: "#202534",
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            {t("loading") ||
              "Loading..."}
          </div>
        );
      }

      const normalizedLang =
        String(
          lang || "en"
        )
          .toLowerCase()
          .split("-")[0];

 const translatedTitle =
  getWorldcupTitle(cup, normalizedLang) ||
  "";

const worldCupKeywordMap = {
  ko: "이상형 월드컵",
  en: "Ideal Type World Cup",
  ja: "人気投票トーナメント",
  zh: "人气投票淘汰赛",
  es: "Torneo de Votación",
  fr: "Tournoi de Vote",
  de: "Abstimmungsturnier",
  pt: "Torneio de Votação",
  ru: "Турнир голосований",
  id: "Turnamen Voting",
  hi: "वोटिंग टूर्नामेंट",
  vi: "Giải đấu bình chọn",
  ar: "بطولة التصويت",
  bn: "ভোটিং টুর্নামেন্ট",
  th: "เกมโหวตแบบทัวร์นาเมนต์",
  tr: "Oylama Turnuvası",

"it":"Torneo delle preferenze",
"pl":"Turniej ulubieńców",
"nl":"Voorkeurstoernooi",
"cs":"Turnaj oblíbených možností"
};

const worldCupKeyword =
  worldCupKeywordMap[normalizedLang] ||
  worldCupKeywordMap.en;

const cleanEnglishTitle = translatedTitle
  .replace(/\s+(Bracket|Tournament)$/i, "")
  .trim();

const pageSeoTitle =
  normalizedLang === "en"
    ? `${cleanEnglishTitle} Tournament Bracket Game | Ideal Type World Cup | OnePickGame`
    : normalizedLang === "ko"
      ? `이상형 월드컵 | ${translatedTitle} | 원픽게임`
      : `${worldCupKeyword} - ${translatedTitle} | OnePickGame`;
const translatedDescription = worldcupMetadata.worldcupDescription(cup, normalizedLang);
      return (
        <>
          <WorldcupDetailSeo cup={cup}
            lang={
              normalizedLang
            }
            slug={`select-round/${cup.id}`}
       title={pageSeoTitle}
            description={
              translatedDescription
            }
          />

  <SelectRoundPage
  cup={cup}
  worldcupList={relatedWorldcupsById[id] || worldcupList}
  user={user}
  isAdmin={isAdmin}
  onDelete={handleWorldcupDelete}
            maxRound={
              cup.data.length
            }
            candidates={
              cup.data
            }
            onSelect={(
              roundOrCandidate
            ) => {
              if (
                typeof roundOrCandidate ===
                "number"
              ) {
                navigate(
                  getLangPath(
                    i18n,
                    `match/${cup.id}/${roundOrCandidate}`
                  )
                );
              } else if (
                typeof roundOrCandidate ===
                  "object" &&
                roundOrCandidate?.id
              ) {
                navigate(
                  getLangPath(
                    i18n,
                    `match/${cup.id}/${cup.data.length}`
                  )
                );
              }
            }}
          />
        </>
      );
    }

function WorldcupMakerWrapper() {
  const { fetchWorldcups, nickname, setWorldcupAllLoaded, user } = useContext(AppShellContext);

  const { currentLang, i18n } = useContext(AppRoutesContext);

  const navigate =
    useNavigate();

  const makerTitleMap = {
  en: "Create a Tournament Bracket Game | OnePickGame",
  ko: "이상형 월드컵 만들기 | 원픽게임",
  ja: "投票トーナメントを作成 | OnePickGame",
  zh: "创建投票淘汰赛 | OnePickGame",
  es: "Crear un Torneo de Votación | OnePickGame",
  fr: "Créer un Tournoi de Vote | OnePickGame",
  de: "Abstimmungsturnier erstellen | OnePickGame",
  pt: "Criar um Torneio de Votação | OnePickGame",
  ru: "Создать турнир с голосованием | OnePickGame",
  id: "Buat Turnamen Voting | OnePickGame",
  hi: "वोटिंग टूर्नामेंट बनाएं | OnePickGame",
  vi: "Tạo Giải Đấu Bình Chọn | OnePickGame",
  ar: "إنشاء بطولة تصويت | OnePickGame",
  bn: "ভোটিং টুর্নামেন্ট তৈরি করুন | OnePickGame",
  th: "สร้างเกมโหวตแบบทัวร์นาเมนต์ | OnePickGame",
  tr: "Oylama Turnuvası Oluştur | OnePickGame",

"it":"Crea un torneo di tornei | OnePickGame",
"pl":"Tworzenie turnieju gry OnePickGame",
"nl":"Maak een toernooi toernooi Spel OnePickGame",
"cs":"Vytvořit turnaj hru OnePickGame"
};

const makerDescMap = {
  en: "Create your own tournament bracket game on OnePickGame. Add candidates, build matchups, share your bracket, and let players vote for their favorites.",

  ko: "나만의 이상형 월드컵을 만들어보세요. 후보를 추가하고 대진을 구성해 공유한 뒤, 다른 사용자들과 함께 좋아하는 후보를 선택할 수 있습니다.",

  ja: "自分だけの投票トーナメントを作成できます。候補を追加して対戦を組み、共有してみんなでお気に入りに投票しましょう。",

  zh: "创建属于自己的投票淘汰赛。添加候选项、生成对战、分享比赛，并让大家为喜欢的候选项投票。",

  es: "Crea tu propio torneo de votación. Añade candidatos, organiza enfrentamientos, comparte el torneo y deja que los jugadores voten por sus favoritos.",

  fr: "Créez votre propre tournoi de vote. Ajoutez des candidats, organisez les duels, partagez le tournoi et laissez les joueurs voter pour leurs favoris.",

  de: "Erstelle dein eigenes Abstimmungsturnier. Füge Kandidaten hinzu, erstelle Duelle, teile das Turnier und lass Spieler für ihre Favoriten abstimmen.",

  pt: "Crie seu próprio torneio de votação. Adicione candidatos, monte os confrontos, compartilhe o torneio e deixe os jogadores votarem em seus favoritos.",

  ru: "Создайте собственный турнир с голосованием. Добавляйте участников, составляйте пары, делитесь турниром и голосуйте за любимых участников.",

  id: "Buat turnamen voting milikmu sendiri. Tambahkan kandidat, susun pertandingan, bagikan turnamen, dan biarkan pemain memilih favorit mereka.",

  hi: "अपना वोटिंग टूर्नामेंट बनाएं। प्रतियोगियों को जोड़ें, मुकाबले तैयार करें, टूर्नामेंट शेयर करें और खिलाड़ियों को अपने पसंदीदा विकल्प के लिए वोट करने दें।",

  vi: "Tạo giải đấu bình chọn của riêng bạn. Thêm ứng viên, tạo các cặp đấu, chia sẻ giải đấu và để người chơi bình chọn cho mục yêu thích.",

  ar: "أنشئ بطولة تصويت خاصة بك. أضف المتسابقين، وأنشئ المواجهات، وشارك البطولة، ودع اللاعبين يصوتون لمفضلاتهم.",

  bn: "নিজের ভোটিং টুর্নামেন্ট তৈরি করুন। প্রতিযোগী যোগ করুন, ম্যাচআপ তৈরি করুন, টুর্নামেন্ট শেয়ার করুন এবং খেলোয়াড়দের পছন্দের প্রতিযোগীকে ভোট দিতে দিন।",

  th: "สร้างเกมโหวตแบบทัวร์นาเมนต์ของคุณเอง เพิ่มผู้เข้าแข่งขัน จัดคู่การแข่งขัน แชร์ทัวร์นาเมนต์ และให้ผู้เล่นโหวตตัวเลือกที่ชื่นชอบ",

  tr: "Kendi oylama turnuvanı oluştur. Adayları ekle, eşleşmeleri hazırla, turnuvayı paylaş ve oyuncuların favorilerine oy vermesini sağla.",

"it":"Crea il tuo torneo su OnePickGame. Aggiungi candidati, costruisci incontri, condividi il tuo torneo e lascia che i giocatori votino per i loro preferiti.",
"pl":"Stwórz własną grę turniejową na OnePickGame. Dodaj kandydatów, budować matchupy, dzielić turniej, i niech gracze głosują na swoich ulubionych.",
"nl":"Creëer je eigen toernooitoernooispel op OnePickGame. Voeg kandidaten toe, bouw matchups, deel je toernooi en laat spelers op hun favorieten stemmen.",
"cs":"Vytvořte si vlastní turnaj turnaj hru na OnePickGame. Přidat kandidáty, stavět matchups, sdílet svůj turnaj, a nechat hráče hlasovat pro své oblíbené."
};

  return (
    <>
      <Seo
        lang={currentLang}
        slug="worldcup-maker"
        title={
          makerTitleMap[currentLang] ||
          makerTitleMap.en
        }
        description={
          makerDescMap[currentLang] ||
          makerDescMap.en
        }
        indexable={false}
      />

      <WorldcupMaker
        fetchWorldcups={
          fetchWorldcups
        }
 onCreate={() => {
  setWorldcupAllLoaded(false);
  navigate(
    getLangPath(
      i18n
    )
  );
}}
        onCancel={() =>
          navigate(
            getLangPath(
              i18n
            )
          )
        }
        user={user}
        nickname={
          nickname
        }
      />
    </>
  );
}

function ManageWorldcupWrapper() {
  const { isAdmin, setWorldcupList, user, worldcupList } = useContext(AppShellContext);

      return (
        <ManageWorldcup
          user={user}
          isAdmin={
            isAdmin
          }
          worldcupList={
            worldcupList
          }
          setWorldcupList={
            setWorldcupList
          }
        />
      );
    }

function EditWorldcupPageWrapper() {
  const { fetchWorldcups, isAdmin, nickname, user, worldcupList } = useContext(AppShellContext);

      const { id } =
        useParams();

      return (
        <EditWorldcupPage
          worldcupList={
            worldcupList
          }
          fetchWorldcups={
            fetchWorldcups
          }
          cupId={id}
          user={user}
          nickname={
            nickname
          }
          isAdmin={
            isAdmin
          }
        />
      );
    }

function AdminRoute() {
  const { isAdmin, nickname, nicknameLoading, t } = useContext(AppShellContext);

  const { i18n } = useContext(AppRoutesContext);

      if (
        nicknameLoading
      ) {
        return (
          <div
            style={{
              padding: 60,
              textAlign:
                "center",
              fontWeight:
                700,
              fontSize: 24,
            }}
          >
            Loading...
          </div>
        );
      }

      if (!isAdmin) {
        return (
          <div
            style={{
              padding: 60,
              textAlign:
                "center",
              fontWeight:
                700,
              fontSize: 24,
            }}
          >
            {t(
              "admin_only"
            ) ||
              "Admins only."}

            <br />

            {t(
              "login_with_admin"
            ) ||
              "Please log in as admin."}
          </div>
        );
      }

      return (
        <>
          <AdminBar
            adminName={
              nickname
            }
            onLogout={() => {
              supabase.auth
                .signOut()
                .then(() => {
                  window.location.href =
                    getLangPath(
                      i18n
                    );
                });
            }}
          />

          <AdminDashboard />
        </>
      );
    }

function AdminStatsRoute() {
  const { isAdmin, nickname, nicknameLoading, t } = useContext(AppShellContext);

  const { i18n } = useContext(AppRoutesContext);

      if (
        nicknameLoading
      ) {
        return (
          <div
            style={{
              padding: 60,
              textAlign:
                "center",
              fontWeight:
                700,
              fontSize: 24,
            }}
          >
            Loading...
          </div>
        );
      }

      if (!isAdmin) {
        return (
          <div
            style={{
              padding: 60,
              textAlign:
                "center",
              fontWeight:
                700,
              fontSize: 24,
            }}
          >
            {t(
              "admin_only"
            ) ||
              "Admins only."}

            <br />

            {t(
              "login_with_admin"
            ) ||
              "Please log in as admin."}
          </div>
        );
      }

      return (
        <>
          <AdminBar
            adminName={
              nickname
            }
            onLogout={() => {
              supabase.auth
                .signOut()
                .then(() => {
                  window.location.href =
                    getLangPath(
                      i18n
                    );
                });
            }}
          />

          <AdminStatsPage />
        </>
      );
    }

function RedirectStatsToResult() {
      const {
        lang,
        id,
      } = useParams();

      return (
        <Navigate
          to={`/${lang}/result/${id}`}
          replace
        />
      );
    }

function App() {
 useIsMobile();
  const [worldcupList, setWorldcupList] = useState([]);
  const [worldcupAllLoaded, setWorldcupAllLoaded] = useState(false);
  const [relatedWorldcupsById, setRelatedWorldcupsById] = useState({});

  const { t, i18n } = useTranslation();

  const [user, setUser] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);

  const [nickname, setNickname] = useState("");

  const [nicknameLoading, setNicknameLoading] =
    useState(false);

  const [fixedWorldcupIds, setFixedWorldcupIds] =
    useState([]);

  const [fixedWorldcups, setFixedWorldcups] =
    useState([]);

  /* ===================================================
     로그인 사용자 + 프로필 확인
  =================================================== */

  useEffect(() => subscribeToAuthProfile(supabase, {
    setUser,
    setNickname,
    setNicknameLoading,
    setIsAdmin,
  }), []);

  /* ===================================================
     닉네임 변경
  =================================================== */

  function updateNickname(nick) {
    setNickname(nick);

    // Nickname edits do not grant or revoke server-maintained administrator roles.
  }

  /* ===================================================
     월드컵 목록 다시 가져오기
  =================================================== */

  const fetchWorldcups = useCallback(async () => {
    const list = await getWorldcupGames();
    setWorldcupList(list);
    setWorldcupAllLoaded(true);
    return list;
  }, []);

  const fetchOneWorldcup = useCallback(async (id) => {
    const [cup, related] = await Promise.all([
      getWorldcupGame(id),
      fetchWorldcupRelatedCards(id).catch(error => { console.warn("Related games could not be loaded", error); return []; }),
    ]);
    setRelatedWorldcupsById(previous => ({ ...previous, [id]: related }));
    setWorldcupList(previous => {
      const games = new Map(previous.map(item => [String(item.id), item]));
      games.set(String(id), cup);
      related.forEach(item => { if (!games.has(String(item.id)) || games.get(String(item.id))._homeCardOnly) games.set(String(item.id), item); });
      return [...games.values()];
    });
    return cup;
  }, []);

  /* ===================================================
     월드컵 삭제

     관리자:
       실제 삭제 X
       deleted_at 설정
       → 관리자 휴지통

     일반 사용자:
       Storage 이미지 삭제
       DB 실제 삭제
  =================================================== */

  const handleWorldcupDelete = async (id, options = {}) => {
    if (!id) {
      throw new Error(
        "월드컵 ID가 없습니다."
      );
    }

    console.log(
      "🔥 월드컵 삭제 요청:",
      {
        id,
        isAdmin,
        nickname,
        mode: isAdmin
          ? "TRASH"
          : "TRASH",
      }
    );

    if (isAdmin) {
      await softDeleteWorldcupGame(id);

      console.log(
        "🗑️ 관리자 삭제 → 휴지통 이동 완료:",
        id
      );
    } else {
      await deleteWorldcupGame(id);

      console.log(
        "🗑️ 사용자 월드컵 휴지통 이동 완료:",
        id
      );
    }

    if (options.refresh !== false) await fetchWorldcups();

    return true;
  };

  /* ===================================================
     최초 월드컵 로딩
  =================================================== */

  // Data is requested by WorldcupRouteDataLoader only when the active route needs it.

  /* ===================================================
     운영자 PICK ID 로딩
  =================================================== */

  useEffect(() => {
    async function fetchFixedWorldcups() {
      try {
        const {
          data,
          error,
        } = await supabase
          .from("fixed_worldcups")
          .select("worldcup_id")
          .order("id", {
            ascending: true,
          });

        if (error) {
          console.error(
            "운영자 PICK 조회 실패:",
            error
          );

          setFixedWorldcupIds([]);
          return;
        }

        if (Array.isArray(data)) {
          setFixedWorldcupIds(
            data.map((d) =>
              String(d.worldcup_id)
            )
          );
        } else {
          setFixedWorldcupIds([]);
        }
      } catch (error) {
        console.error(
          "운영자 PICK 조회 오류:",
          error
        );

        setFixedWorldcupIds([]);
      }
    }

    fetchFixedWorldcups();
  }, []);

  /* ===================================================
     운영자 PICK 실제 월드컵 데이터 구성
  =================================================== */

  useEffect(() => {
    if (
      !worldcupList.length ||
      !fixedWorldcupIds.length
    ) {
      setFixedWorldcups([]);
      return;
    }

    const fixeds =
      fixedWorldcupIds
        .map((id) =>
          worldcupList.find(
            (cup) =>
              String(cup.id) ===
              String(id)
          )
        )
        .filter(Boolean);

    setFixedWorldcups(fixeds);
  }, [
    worldcupList,
    fixedWorldcupIds,
  ]);

 

  /* ===================================================
     언어 변경
  =================================================== */

  function handleLangChange(
    lng,
    _options
  ) {
    i18n.changeLanguage(lng);

    localStorage.setItem(
      "onepickgame_lang",
      lng
    );
  }
    /* ===================================================
     백업
  =================================================== */

  function handleBackup() {
    const data =
      JSON.stringify(
        worldcupList || []
      );

    const blob =
      new Blob([data], {
        type: "application/json",
      });

    const url =
      URL.createObjectURL(blob);

    const a =
      document.createElement("a");

    a.href = url;

    a.download =
      `worldcup_backup_${Date.now()}.json`;

    a.click();

    URL.revokeObjectURL(url);
  }

  /* ===================================================
     복구
     현재는 프론트 목록만 복원
     DB에는 직접 반영하지 않음
  =================================================== */

  function handleRestore(e) {
    const file =
      e.target.files[0];

    if (!file) return;

    const reader =
      new FileReader();

    reader.onload = function (ev) {
      try {
        const data =
          JSON.parse(
            ev.target.result
          );

        if (
          !Array.isArray(data)
        ) {
          throw new Error(
            "Invalid format"
          );
        }

        setWorldcupList(data);

        alert(
          t("restore_success") ||
            "Restore successful! (Front-end only, DB not affected)"
        );
      } catch (error) {
        console.error(
          "복구 실패:",
          error
        );

        alert(
          t("restore_fail") ||
            "Restore failed!"
        );
      }
    };

    reader.readAsText(file);

    e.target.value = "";
  }

  /* ===================================================
     내가 만든 월드컵
  =================================================== */

  

  /* ===================================================
     최근 본 월드컵
  =================================================== */

  

  /* ===================================================
     통계 페이지
  =================================================== */

  

  /* ===================================================
     앱 라우트
  =================================================== */

  

  /* ===================================================
     최종 APP 렌더링
  =================================================== */

  return (
    <AppShellContext.Provider value={{ fetchWorldcups, fixedWorldcups, handleBackup, handleLangChange, handleRestore, handleWorldcupDelete, i18n, isAdmin, nickname, nicknameLoading, relatedWorldcupsById, setUser, setWorldcupAllLoaded, setWorldcupList, t, updateNickname, user, worldcupList }}><AdExclusionProvider><Router>
    <div
      className="app-main-wrapper"
      style={{
        margin: 0,
        padding: 0,
        minHeight: "100vh",
position: "relative",
overflowX: "hidden",
      }}
    >
      <RouteBackground />


      <div
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1,
          pointerEvents: "none",
          background: "rgba(0,0,0,0.0)",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          className="main-content-outer"
          style={{
            paddingTop: 0,
            margin: 0,
          }}
        >
  <ScrollToTopOnRouteChange />

  <WorldcupRouteDataLoader worldcupList={worldcupList} allLoaded={worldcupAllLoaded} loadAll={fetchWorldcups} loadOne={fetchOneWorldcup}>
    <AppRoutes />
  </WorldcupRouteDataLoader>

  <ScrollToTopButton />

  <Footer />
        </div>
      </div>
    </div>
    </Router></AdExclusionProvider></AppShellContext.Provider>
  );
}

export default App;



